/**
 * Wire envelope for the Tìm Buýt `SearchBuyt` API.
 *
 * Extracted verbatim from `timbusApi.ts`, which previously interleaved three
 * concerns: this codec, the `VehicleState` request shape, and vehicle parsing.
 * Three upstream commands now need the codec (`VehicleState`,
 * `RouteBuytOptionV3`, `Geo2Address`), so it lives on its own.
 *
 * Every command is `…/SearchBuyt/<Command>?ReqParams=<envelope>`, where the
 * envelope is XOR-encrypted with a random key, followed by both keys, their
 * lengths, and a checksum byte, all base64-encoded.
 */

// ConnectionExecute prepends PREFS_CLIENT_ID to every encrypted request.
// VehicleState returns an empty list for the unregistered value 0, while the
// service accepts a stable nonzero public client ID without an account token.
const TIMBUS_PUBLIC_CLIENT_ID = 1

export class ByteWriter {
  readonly bytes: number[] = []

  int(value: number) {
    const buffer = new ArrayBuffer(4)
    new DataView(buffer).setInt32(0, value, true)
    this.raw(new Uint8Array(buffer))
  }

  shortString(value?: string) {
    const encoded = new TextEncoder().encode(value || '')
    const buffer = new ArrayBuffer(2)
    new DataView(buffer).setUint16(0, encoded.length, true)
    this.raw(new Uint8Array(buffer))
    this.raw(encoded)
  }

  byte(value: number) {
    this.bytes.push(value & 0xff)
  }

  /** float32 LE. Used by the RouteBuytOptionV3 request; VehicleState has no floats. */
  float(value: number) {
    const buffer = new ArrayBuffer(4)
    new DataView(buffer).setFloat32(0, value, true)
    this.raw(new Uint8Array(buffer))
  }

  raw(value: Uint8Array) {
    this.bytes.push(...value)
  }

  build() {
    return Uint8Array.from(this.bytes)
  }
}

export class ByteReader {
  private offset = 0
  private readonly view: DataView

  constructor(private readonly bytes: Uint8Array) {
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  }

  remaining() {
    return this.bytes.length - this.offset
  }

  unsignedByte() {
    const value = this.view.getUint8(this.offset)
    this.offset += 1
    return value
  }

  signedByte() {
    const value = this.view.getInt8(this.offset)
    this.offset += 1
    return value
  }

  int() {
    const value = this.view.getInt32(this.offset, true)
    this.offset += 4
    return value
  }

  /** int16 LE. Used by the RouteBuytOptionV3 response; VehicleState has no int16. */
  short() {
    const value = this.view.getInt16(this.offset, true)
    this.offset += 2
    return value
  }

  float() {
    const value = this.view.getFloat32(this.offset, true)
    this.offset += 4
    return value
  }

  string(lengthBytes: 1 | 2) {
    const length =
      lengthBytes === 1 ? this.unsignedByte() : this.view.getUint16(this.advance(2), true)
    const start = this.offset
    this.offset += length
    return new TextDecoder().decode(this.bytes.subarray(start, this.offset))
  }

  private advance(length: number) {
    const start = this.offset
    this.offset += length
    return start
  }
}

function createKey() {
  const length = 32 + Math.floor(Math.random() * 33)
  return Uint8Array.from({ length }, () => 0x30 + Math.floor(Math.random() * 0x50))
}

function checksum(bytes: Uint8Array) {
  let value = 0
  for (const byte of bytes) {
    value = (value + (byte > 127 ? byte - 256 : byte)) & 0xff
  }
  return value
}

function concatenate(...parts: Uint8Array[]) {
  const output = new Uint8Array(parts.reduce((size, part) => size + part.length, 0))
  let offset = 0
  for (const part of parts) {
    output.set(part, offset)
    offset += part.length
  }
  return output
}

function intBytes(value: number) {
  const bytes = new Uint8Array(4)
  new DataView(bytes.buffer).setInt32(0, value, true)
  return bytes
}

function toBase64(bytes: Uint8Array) {
  let value = ''
  for (const byte of bytes) value += String.fromCharCode(byte)
  return btoa(value)
}

function fromBase64(value: string) {
  const decoded = atob(value)
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0))
}

export function encodeRequest(payload: Uint8Array) {
  const requestKey = createKey()
  const responseKey = createKey()
  const encrypted = concatenate(intBytes(TIMBUS_PUBLIC_CLIENT_ID), payload)

  for (let index = 0; index < encrypted.length; index += 1) {
    encrypted[index] ^= requestKey[index % requestKey.length]
  }

  const body = concatenate(
    encrypted,
    requestKey,
    Uint8Array.of(requestKey.length),
    responseKey,
    Uint8Array.of(responseKey.length),
  )
  return toBase64(concatenate(body, Uint8Array.of(checksum(body))))
}

export function decodeResponse(value: unknown) {
  if (typeof value !== 'string') throw new Error('VehicleState returned invalid JSON')
  const envelope = fromBase64(value)
  if (envelope.length < 67) throw new Error('VehicleState response is too short')
  if (checksum(envelope.subarray(0, -1)) !== envelope.at(-1)) {
    throw new Error('VehicleState response checksum failed')
  }

  const responseKeyLength = envelope.at(-2)!
  const requestKeyLengthIndex = envelope.length - 3 - responseKeyLength
  const requestKeyLength = envelope[requestKeyLengthIndex]
  const payloadLength = requestKeyLengthIndex - requestKeyLength
  if (payloadLength < 0 || requestKeyLength === 0) {
    throw new Error('VehicleState response envelope is invalid')
  }

  const key = envelope.subarray(payloadLength, requestKeyLengthIndex)
  const payload = envelope.slice(0, payloadLength)
  for (let index = 0; index < payload.length; index += 1) {
    payload[index] ^= key[index % key.length]
  }
  return payload
}
